/**
 * Gallery — fullscreen "My Creations" library view.
 *
 * Unified grid showing both comics and projects sorted by last-updated,
 * with horizontal card layout matching the Figma design (KEYSTONE-1305).
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  View,
  Text,
  Pressable,
  ScrollView,
  Image,
  ActivityIndicator,
  Platform,
  BackHandler,
  LayoutChangeEvent,
} from "react-native";
import { styles, COLUMNS, COLUMN_GAP, CARD_HEIGHT } from "./Gallery.styles";
import { colors } from "../styles";
import { useESSStore, ESSProject, ESSComic } from "../store/essStore";
import { useSocketStore } from "../store/socketStore";
import { beginDeleteBatch } from "../store/galleryDeleteToast";
import { useNavigation } from "../navigation/useNavigation";
import Avatar from "./Avatar";
import UndoIcon from "../assets/icons/undo.svg";
import OfflineToast from "./OfflineToast";
import { useNetworkStore } from "../store/networkStore";
import { useConfigStore } from "../store/configStore";
import { trackView, trackButton, View as MetricView, Button } from "../services/metrics";

const MIN_CARD_WIDTH = 280;
const IMAGE_WIDTH_RATIO = 0.4;
const SCROLL_THRESHOLD = 400;
const SCROLL_ACTIVATE_OFFSET = 50;
const LOAD_MORE_TIMEOUT_MS = 10000;
const SCROLL_EVENT_THROTTLE = 16;



function base64ToUri(b64?: string): string | undefined {
  if (!b64) return undefined;
  if (b64.startsWith("data:")) return b64;
  return `data:image/jpeg;base64,${b64}`;
}

type LibraryItem =
  | { type: "project"; data: ESSProject }
  | { type: "comic"; data: ESSComic };

export default function Gallery() {
  const { navigate, goBack } = useNavigation();
  const projects = useESSStore((s) => s.projects);
  const projectsNext = useESSStore((s) => s.projectsNextToken);
  const comics = useESSStore((s) => s.comics);
  const comicsNext = useESSStore((s) => s.comicsNextToken);

  const sharingEnabled = useConfigStore((s) => s.featureFlags.sharing);
  const sharedComics = useESSStore((s) => s.sharedComics);

  const isConnected = useSocketStore((s) => s.isConnected);
  const isOffline = useNetworkStore((s) => s.isOffline);
  const {
    listESSProjects,
    loadMoreESSProjects,
    listESSComics,
    loadMoreESSComics,
    listSharedComics,
  } = useSocketStore();

  // Whether to surface the "shared with you" card at all.
  const showShared = sharingEnabled && sharedComics.length > 0;

  // Most-recent-first shared comics: drive the cover (latest) and up to two
  // distinct sender avatars for the card footer.
  const sortedShared = useMemo(
    () => [...sharedComics].sort((a, b) => b.sharedAt - a.sharedAt),
    [sharedComics],
  );
  const latestSharedCover = sortedShared[0]?.coverImageUrl;
  // Up to two distinct most-recent senders (name + optional avatar). Each
  // renders via <Avatar>, so a sender without an image still shows an initial.
  const sharedSenders = useMemo(() => {
    const seen = new Set<string>();
    const senders: { name?: string; uri?: string }[] = [];
    for (const c of sortedShared) {
      const key = c.senderName || c.senderAvatarUri;
      if (!key || seen.has(key)) continue;
      seen.add(key);
      senders.push({ name: c.senderName, uri: c.senderAvatarUri });
      if (senders.length === 2) break;
    }
    return senders;
  }, [sortedShared]);

  const [closing, setClosing] = useState(false);
  const [cardWidth, setCardWidth] = useState(0);
  const [initialLoaded, setInitialLoaded] = useState(false);

  const [deleteMode, setDeleteMode] = useState(false);
  // Selection carries each item's type so delete routes to the right API without
  // re-deriving it from a list that may have refreshed mid-selection.
  const [selected, setSelected] = useState<Map<string, "project" | "comic">>(new Map());

  const exitDeleteMode = useCallback(() => {
    setDeleteMode(false);
    setSelected(new Map());
  }, []);

  const toggleSelected = useCallback((id: string, type: "project" | "comic") => {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(id)) next.delete(id); else next.set(id, type);
      return next;
    });
  }, []);

  const handleDeleteSelected = useCallback(() => {
    if (selected.size === 0) return;
    trackButton(MetricView.Gallery, Button.Gallery.DeleteSelected);
    const { deleteESSProject, deleteESSComic } = useSocketStore.getState();
    const entries = [...selected.entries()];
    beginDeleteBatch(entries.map(([id]) => id));
    for (const [id, type] of entries) {
      if (type === "project") deleteESSProject(id);
      else deleteESSComic(id);
    }
    exitDeleteMode();
  }, [selected, exitDeleteMode]);

  const mountedRef = useRef(false);
  const loadingMoreProjectsRef = useRef(false);
  const loadingMoreComicsRef = useRef(false);

  const handleClose = useCallback(() => {
    setClosing(true);
    // Return to wherever the gallery was opened from (landing or builder) — the
    // back stack knows.
    requestAnimationFrame(() => goBack());
  }, [goBack]);

  const onGridLayout = useCallback((e: LayoutChangeEvent) => {
    const w = e.nativeEvent.layout.width;
    const cols = Math.max(1, Math.min(COLUMNS, Math.floor((w + COLUMN_GAP) / (MIN_CARD_WIDTH + COLUMN_GAP))));
    const next = (w - (cols - 1) * COLUMN_GAP) / cols;
    setCardWidth(Math.max(MIN_CARD_WIDTH, next));
  }, []);

  useEffect(() => {
    trackView(MetricView.Gallery);
    setInitialLoaded(false);
    listESSProjects();
    listESSComics();

    const socketInstance = useSocketStore.getState().socket;
    if (!socketInstance) {
      setInitialLoaded(true);
      return;
    }

    let gotProjects = false;
    let gotComics = false;
    const maybeDone = () => {
      if (gotProjects && gotComics) setInitialLoaded(true);
    };
    const onProjects = () => { gotProjects = true; maybeDone(); };
    const onComics = () => { gotComics = true; maybeDone(); };

    socketInstance.once("essProjectList", onProjects);
    socketInstance.once("essComicList", onComics);
    const safety = setTimeout(() => setInitialLoaded(true), 8000);

    return () => {
      socketInstance.off("essProjectList", onProjects);
      socketInstance.off("essComicList", onComics);
      clearTimeout(safety);
    };
  }, []);

  useEffect(() => {
    const s = useSocketStore.getState().socket;
    if (!s) return;
    const onComics = () => { loadingMoreComicsRef.current = false; };
    const onProjects = () => { loadingMoreProjectsRef.current = false; };
    s.on("essComicList", onComics);
    s.on("essProjectList", onProjects);
    return () => {
      s.off("essComicList", onComics);
      s.off("essProjectList", onProjects);
    };
  }, []);

  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }
    if (isConnected) {
      listESSProjects();
      listESSComics();
    }
  }, [isConnected]);

  // Load shared comics once sharing is enabled and connected. Keyed on the flag
  // so it also fires if the handshake enables sharing after the gallery mounts.
  useEffect(() => {
    if (sharingEnabled && isConnected) listSharedComics();
  }, [sharingEnabled, isConnected]);

  useEffect(() => {
    if (Platform.OS !== "web") return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [handleClose]);

  // Android hardware back → leave the gallery (back to wherever it was opened
  // from). goBack is read through a ref so the deps:[] listener stays current.
  const goBackRef = useRef(goBack);
  goBackRef.current = goBack;
  useEffect(() => {
    if (Platform.OS === "web") return;
    const sub = BackHandler.addEventListener("hardwareBackPress", () => {
      setClosing(true);
      requestAnimationFrame(() => goBackRef.current());
      return true;
    });
    return () => sub.remove();
  }, []);

  const handleOpenProject = (p: ESSProject) => {
    trackButton(MetricView.Gallery, Button.Gallery.OpenProject);
    useSocketStore.getState().loadESSProject(p.storyId);
    // loadESSProject resets to the builder; the project preview shows there.
  };

  const handleOpenComic = (c: ESSComic) => {
    trackButton(MetricView.Gallery, Button.Gallery.OpenComic);
    // Open the comic reader page; its back returns here via the stack.
    navigate("reader", { comic: c.comicId });
  };

  const items = useMemo<LibraryItem[]>(() => {
    // When offline, only show comics (projects can't be opened without a connection)
    const all = [
      ...(!isOffline ? projects.map((p): LibraryItem => ({ type: "project", data: p })) : []),
      ...comics.map((c): LibraryItem => ({ type: "comic", data: c })),
    ].sort((a, b) => b.data.updatedAt - a.data.updatedAt);

    // When any list is still paginating, hide items that the unloaded pages
    // might interlace with. The cutoff is the oldest loaded item from the
    // list that still has more pages — anything older from the other list
    // could have unloaded items that belong before it.
    if (!isOffline && projects.length > 0 && comics.length > 0) {
      const cutoffs: number[] = [];
      if (projectsNext) cutoffs.push(projects[projects.length - 1].updatedAt);
      if (comicsNext) cutoffs.push(comics[comics.length - 1].updatedAt);
      if (cutoffs.length > 0) {
        const cutoff = Math.max(...cutoffs);
        return all.filter((item) => item.data.updatedAt >= cutoff);
      }
    }

    return all;
  }, [projects, comics, projectsNext, comicsNext, isOffline]);

  // Drop selections whose items are no longer present after a list refresh, so a
  // stale id can't be sent to delete or inflate the "N selected" count.
  useEffect(() => {
    if (!deleteMode) return;
    setSelected((prev) => {
      const present = new Set(items.map((i) => (i.type === "project" ? i.data.storyId : (i.data as ESSComic).comicId)));
      let changed = false;
      const next = new Map(prev);
      for (const id of next.keys()) {
        if (!present.has(id)) { next.delete(id); changed = true; }
      }
      return changed ? next : prev;
    });
  }, [items, deleteMode]);

  const hasMore = !isOffline && !!(projectsNext || comicsNext);

  const [loadingMore, setLoadingMore] = useState(false);

  const handleLoadMore = useCallback(() => {
    if (!hasMore) return;
    let fired = false;
    if (projectsNext && !loadingMoreProjectsRef.current) {
      loadingMoreProjectsRef.current = true;
      loadMoreESSProjects(projectsNext);
      fired = true;
    }
    if (comicsNext && !loadingMoreComicsRef.current) {
      loadingMoreComicsRef.current = true;
      loadMoreESSComics(comicsNext);
      fired = true;
    }
    if (fired) {
      setLoadingMore(true);
      setTimeout(() => {
        loadingMoreProjectsRef.current = false;
        loadingMoreComicsRef.current = false;
        setLoadingMore(false);
      }, LOAD_MORE_TIMEOUT_MS);
    }
  }, [projectsNext, comicsNext, hasMore]);

  const viewportHeightRef = useRef(0);
  const contentHeightRef = useRef(0);
  const maybeFillViewport = useCallback((contentHeight: number) => {
    contentHeightRef.current = contentHeight;
    if (loadingMoreProjectsRef.current || loadingMoreComicsRef.current) return;
    if (contentHeight > 0 && viewportHeightRef.current > 0 && contentHeight <= viewportHeightRef.current) {
      handleLoadMore();
    }
  }, [handleLoadMore]);

  useEffect(() => {
    if (loadingMore && !loadingMoreProjectsRef.current && !loadingMoreComicsRef.current) {
      setLoadingMore(false);
      maybeFillViewport(contentHeightRef.current);
    }
  }, [projects.length, comics.length, loadingMore, maybeFillViewport]);

  const userScrolledRef = useRef(false);
  const maybeLoadMore = useCallback((e: any) => {
    const { layoutMeasurement, contentOffset, contentSize } = e.nativeEvent;
    // Only trigger after user has scrolled at least once (not on initial layout)
    if (contentOffset.y > SCROLL_ACTIVATE_OFFSET) userScrolledRef.current = true;
    if (!userScrolledRef.current) return;
    const distanceFromBottom = contentSize.height - layoutMeasurement.height - contentOffset.y;
    if (distanceFromBottom < SCROLL_THRESHOLD) {
      handleLoadMore();
    }
  }, [handleLoadMore]);

  const handleViewportLayout = useCallback((e: LayoutChangeEvent) => {
    viewportHeightRef.current = e.nativeEvent.layout.height;
  }, []);

  return (
    <View style={styles.container}>
      <OfflineToast variant="dark" />
      <View style={styles.header}>
        <View style={styles.headerLeft}>
          <Pressable
            style={[styles.backBtn, closing && styles.closeBtnClosing]}
            onPress={handleClose}
            accessibilityLabel="Back"
            accessibilityRole="button"
          >
            <Image source={require("../assets/icons/caret-left.png")} style={styles.backIcon} resizeMode="contain" />
          </Pressable>
          {deleteMode ? (
            <View>
              <Text style={[styles.title, styles.deleteTitle]}>Delete</Text>
              <Text style={styles.deleteSubtitle}>{selected.size} selected</Text>
            </View>
          ) : (
            <Text style={styles.title}>My Creations</Text>
          )}
        </View>

        {deleteMode ? (
          <View style={styles.headerActions}>
            <Pressable
              style={styles.cancelPill}
              onPress={() => { trackButton(MetricView.Gallery, Button.Gallery.CancelDeleteMode); exitDeleteMode(); }}
            >
              <UndoIcon width={20} height={20} color={colors.white} />
              <Text style={styles.cancelPillText}>Cancel</Text>
            </Pressable>
            <Pressable
              style={[styles.deletePill, selected.size === 0 && styles.deletePillDisabled]}
              disabled={selected.size === 0}
              onPress={handleDeleteSelected}
            >
              <Image source={require("../assets/icons/ic-delete.png")} style={styles.deletePillIcon} resizeMode="contain" />
              <Text style={styles.deletePillText}>Delete</Text>
            </Pressable>
          </View>
        ) : (
          items.length > 0 && (
            <Pressable
              style={styles.deleteModeBtn}
              onPress={() => { trackButton(MetricView.Gallery, Button.Gallery.EnterDeleteMode); setDeleteMode(true); }}
              accessibilityLabel="Delete creations"
              accessibilityRole="button"
            >
              <Image source={require("../assets/icons/ic-delete.png")} style={styles.deleteModeBtnIcon} resizeMode="contain" />
            </Pressable>
          )
        )}
      </View>

      {!initialLoaded ? (
        <View style={styles.initialLoader}>
          <ActivityIndicator size="large" color={colors.textMuted} />
          <Text style={styles.initialLoaderText}>Loading your creations...</Text>
        </View>
      ) : (
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={styles.scrollContent}
          onScroll={maybeLoadMore}
          onMomentumScrollEnd={maybeLoadMore}
          onScrollEndDrag={maybeLoadMore}
          onLayout={handleViewportLayout}
          onContentSizeChange={(_w, h) => maybeFillViewport(h)}
          scrollEventThrottle={SCROLL_EVENT_THROTTLE}
        >
          {items.length === 0 && !showShared ? (
            <Text style={styles.emptyText}>No creations yet</Text>
          ) : (
            <View style={styles.grid} onLayout={onGridLayout}>
              {showShared && cardWidth > 0 && (
                  <Pressable
                    style={[styles.sharedCard, { width: cardWidth, height: CARD_HEIGHT }, deleteMode && styles.cardDisabled]}
                    disabled={deleteMode}
                    onPress={() => { trackButton(MetricView.Gallery, Button.Gallery.SharedWithYou); navigate("sharedGallery"); }}
                  >
                    <View style={styles.sharedCardCover}>
                      {latestSharedCover ? (
                        <Image
                          source={{ uri: latestSharedCover }}
                          style={styles.sharedCardCoverImage}
                          resizeMode="cover"
                        />
                      ) : (
                        <View style={styles.sharedCardCoverPlaceholder}>
                          <Text style={styles.sharedCardCoverEmoji}>{"📖"}</Text>
                        </View>
                      )}
                    </View>
                    <View style={styles.sharedCardFooter}>
                      <Text style={styles.sharedCardTitle} numberOfLines={1}>Stories shared with you</Text>
                      {sharedSenders.length > 0 && (
                        <View style={styles.sharedCardAvatars}>
                          {sharedSenders.map((sender, i) => (
                            <Avatar
                              key={sender.name ?? sender.uri ?? i}
                              size={32}
                              name={sender.name}
                              uri={sender.uri}
                              // First avatar sits on top, pulled over the second.
                              style={[
                                i < sharedSenders.length - 1 && styles.sharedCardAvatarOverlap,
                                { zIndex: sharedSenders.length - i },
                              ]}
                            />
                          ))}
                        </View>
                      )}
                    </View>
                  </Pressable>
              )}
              {cardWidth > 0 && items.map((item) => {
                const key = item.type === "project" ? item.data.storyId : (item.data as ESSComic).comicId;
                return (
                  <LibraryCard
                    key={key}
                    item={item}
                    cardWidth={cardWidth}
                    deleteMode={deleteMode}
                    selected={selected.has(key)}
                    onOpen={() => {
                      if (deleteMode) { toggleSelected(key, item.type); return; }
                      if (item.type === "project") handleOpenProject(item.data as ESSProject);
                      else handleOpenComic(item.data as ESSComic);
                    }}
                  />
                );
              })}
            </View>
          )}

          {loadingMore && (
            <View style={styles.loadingMore}>
              <ActivityIndicator size="small" color={colors.galleryOnSurface} />
              <Text style={styles.loadingMoreText}>Loading more...</Text>
            </View>
          )}
        </ScrollView>
      )}
    </View>
  );
}

interface LibraryCardProps {
  item: LibraryItem;
  cardWidth: number;
  deleteMode: boolean;
  selected: boolean;
  onOpen: () => void;
}

function LibraryCard({ item, cardWidth, deleteMode, selected, onOpen }: LibraryCardProps) {
  const title = item.type === "project"
    ? (item.data as ESSProject).title || "Untitled Story"
    : (item.data as ESSComic).title || "Untitled Comic";

  const coverUri = base64ToUri(
    item.type === "project"
      ? (item.data as ESSProject).coverImageBase64
      : (item.data as ESSComic).coverImageBase64,
  );

  // Image takes ~40% of the card width
  const imageWidth = Math.round(cardWidth * IMAGE_WIDTH_RATIO);

  return (
    <Pressable style={[styles.card, { width: cardWidth }]} onPress={onOpen}>
      <View style={[styles.cardImage, { width: imageWidth }]}>
        {coverUri && (
          <Image source={{ uri: coverUri }} style={styles.coverImage} resizeMode="cover" />
        )}
      </View>

      <View style={styles.cardContent}>
        <View style={styles.cardTitleArea}>
          <Text style={styles.cardTitle} numberOfLines={3}>
            {title}
          </Text>
        </View>

        {/* Pill stays (dimmed) in delete mode so project vs comic is still distinguishable. */}
        <View style={[styles.cardActions, deleteMode && styles.cardActionsDisabled]}>
          {item.type === "project" ? (
            <View style={styles.editPillBtn}>
              <Image source={require("../assets/icons/ic-edit.png")} style={styles.editPillIcon} resizeMode="contain" />
              <Text style={styles.editPillText}>Edit</Text>
            </View>
          ) : (
            <View style={styles.editPillBtn}>
              <Image source={require("../assets/icons/book-open-white.png")} style={styles.editPillIcon} resizeMode="contain" />
              <Text style={styles.editPillText}>Read</Text>
            </View>
          )}
        </View>
      </View>

      {deleteMode && (
        <View style={[styles.cardCheckbox, styles.checkbox, selected ? styles.checkboxChecked : styles.checkboxUnchecked]}>
          {selected && <View style={styles.checkMark} />}
        </View>
      )}
    </Pressable>
  );
}
